import { format as dateFnsFormat } from 'date-fns';
import { toZonedTime, format as formatTz } from 'date-fns-tz';

/** Common US and North America timezones for settings dropdowns. Same list as installer Account defaults. */
export const TIMEZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Phoenix',
  'America/Anchorage',
  'Pacific/Honolulu',
  'America/Toronto',
  'America/Vancouver',
  'America/Edmonton',
  'America/St_Johns',
  'UTC',
] as const;

/**
 * Format a date in the specified timezone
 * Falls back to browser local time if timezone is invalid
 */
export function formatInTimezone(
  date: Date | string | number,
  formatStr: string,
  timezone: string = 'America/New_York'
): string {
  try {
    const dateObj = typeof date === 'string' || typeof date === 'number' 
      ? new Date(date) 
      : date;
    
    const zonedDate = toZonedTime(dateObj, timezone);
    return formatTz(zonedDate, formatStr, { timeZone: timezone });
  } catch (e) {
    // Fallback to local time if timezone is invalid
    console.warn(`Invalid timezone "${timezone}", falling back to local:`, e);
    const dateObj = typeof date === 'string' || typeof date === 'number' 
      ? new Date(date) 
      : date;
    return dateFnsFormat(dateObj, formatStr);
  }
}

/**
 * Get the current time in a specific timezone
 */
export function nowInTimezone(timezone: string = 'America/New_York'): Date {
  try {
    return toZonedTime(new Date(), timezone);
  } catch {
    return new Date();
  }
}

/**
 * Format a time-only string (HH:mm) in the specified timezone
 */
export function formatTimeInTimezone(
  date: Date | string | number,
  timezone: string = 'America/New_York'
): string {
  return formatInTimezone(date, 'h:mm a', timezone);
}

/**
 * Format a date for chart axis labels based on range
 */
export function formatChartTime(
  date: Date | string | number,
  range: '24h' | '7d' | '30d',
  timezone: string = 'America/New_York'
): string {
  const formatStr = range === '24h' ? 'h:mm a' : 'MMM d h:mm a';
  return formatInTimezone(date, formatStr, timezone);
}
